import { atom, useSetAtom } from 'jotai'
import { atomFamily, selectAtom } from 'jotai/utils'

export type HistorySelectItem = {
  id: string
  type: 'chat' | 'audio-note' | 'completed-action'
}

export function historySelectionKey(item: HistorySelectItem): string {
  return `${item.type}:${item.id}`
}

/**
 * Source of truth for history multi-select. A Map keyed by `type:id` gives O(1)
 * membership checks and retains the item payloads (for bulk delete / breakdown)
 * without scanning an array. Per-item membership is exposed via
 * `isHistoryItemSelectedAtom` so each checkbox subscribes to only its own key —
 * selecting one row no longer re-renders every other visible checkbox.
 */
export const historySelectionAtom = atom(new Map<string, HistorySelectItem>())

export const isHistoryItemSelectedAtom = atomFamily((key: string) =>
  selectAtom(historySelectionAtom, (selection) => selection.has(key)),
)

// atomFamily caches never evict; lazy TTL eviction bounds the cache over a
// long-lived session. Recreating an evicted entry is safe — it's a pure
// derivation over historySelectionAtom with no state of its own.
const SELECTED_ATOM_TTL_MS = 60 * 60 * 1000
isHistoryItemSelectedAtom.setShouldRemove((createdAt) => Date.now() - createdAt > SELECTED_ATOM_TTL_MS)

export const historySelectedCountAtom = atom((get) => get(historySelectionAtom).size)

export const historyHasSelectionAtom = atom((get) => get(historySelectionAtom).size > 0)

export const historySelectedItemsListAtom = atom((get) => Array.from(get(historySelectionAtom).values()))

export const toggleHistorySelectedItemAtom = atom(null, (get, set, item: HistorySelectItem) => {
  const key = historySelectionKey(item)
  const next = new Map(get(historySelectionAtom))
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.set(key, item)
  }
  set(historySelectionAtom, next)
})

const addHistorySelectedItemsAtom = atom(null, (get, set, items: Array<HistorySelectItem>) => {
  const current = get(historySelectionAtom)
  let changed = false
  const next = new Map(current)
  for (const item of items) {
    const key = historySelectionKey(item)
    if (!next.has(key)) {
      next.set(key, item)
      changed = true
    }
  }
  if (changed) {
    set(historySelectionAtom, next)
  }
})
export const useAddHistorySelectedItems = () => useSetAtom(addHistorySelectedItemsAtom)

export const historySelectedBreakdownAtom = atom((get) => {
  let chats = 0
  let audioNotes = 0
  let actions = 0
  for (const item of get(historySelectionAtom).values()) {
    if (item.type === 'chat') chats++
    else if (item.type === 'audio-note') audioNotes++
    else if (item.type === 'completed-action') actions++
  }
  return { chats, audioNotes, actions }
})

export const clearHistorySelectionAtom = atom(null, (_get, set) => {
  set(historySelectionAtom, new Map())
})
